/**
 * RTL (Right-to-Left) language support utilities
 */

// RTL languages using 3-letter codes (ISO 639-2/639-3)
const RTL_LANGUAGES_3_LETTER = ['ara', 'heb', 'fas', 'prs', 'urd', 'pus', 'kur', 'uig', 'yid'];

// RTL languages using 2-letter codes (ISO 639-1)
const RTL_LANGUAGES_2_LETTER = ['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'ug', 'yi'];

// NOTE: We use 3 letters for translations and 2 letters for i18n

/**
 * Check if a language is RTL based on its language code
 * @param languageCode - Can be 2-letter (ISO 639-1) or 3-letter (ISO 639-2/639-3) code
 * @returns boolean indicating if the language is RTL
 */
export function isRightToLeft(languageCode: string): boolean {
  if (!languageCode) return false;

  const code = languageCode.toLowerCase();

  // Check 2-letter codes first (most common for UI locales)
  if (RTL_LANGUAGES_2_LETTER.includes(code)) {
    return true;
  }

  // Check 3-letter codes (used for translation languages)
  if (RTL_LANGUAGES_3_LETTER.includes(code)) {
    return true;
  }

  return false;
}

/**
 * Get the text direction for a language
 * @param languageCode - Can be 2-letter or 3-letter ISO code
 */
export function getRtlDirection(languageCode: string): 'rtl' | 'ltr' {
  return isRightToLeft(languageCode) ? 'rtl' : 'ltr';
}

/**
 * Creates RTL-aware properties for a given language ID
 * Useful for components that don't use React hooks (like server components)
 */
export function createRtlProps(languageId?: string) {
  const isRtl = languageId ? isRightToLeft(languageId) : false;
  const direction = languageId ? getRtlDirection(languageId) : 'ltr';

  return {
    isRtl,
    direction,
    textAlign: isRtl ? 'text-right' : 'text-left',
    dirProps: { dir: direction },
    className: direction
  };
}
